import {
  BreadcrumbHeading,
  CardGrid,
  MenuIcon,
  MultiSelect,
  Plus,
  PrimaryButton,
  Spinner,
  Typography,
} from '@weareopenr/component-library';
import MenuModal from '../../components/MenuModal';
import { IMenu } from '../../types/menu.types';
import MenuCard, { MENU_CARD_WIDTH } from '../../components/MenuCard';
import EmptyState from '../../components/EmptyState';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { useEffect, useMemo, useState } from 'react';
import { useMenus } from '../../hooks/useMenus';
import { useMenuLabels } from 'src/hooks/useMenuLabels';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { filterByMenuLabels, toMenuLabelFilterOptions } from 'src/utils/menuLabels/menuLabelFilters';
import styles from './Menus.module.scss';

const Menus = () => {
  const { data: menus, isFetching: isMenusFetching } = useMenus();
  const { data: menuLabels } = useMenuLabels();
  const [isMenuModalOpen, setIsMenuModalOpen] = useState<boolean>(false);
  const [selectedMenuLabelIds, setSelectedMenuLabelIds] = useState<string[]>([]);
  const { trackEvent } = useUserTracking();

  const menuLabelFilterOptions = useMemo(() => toMenuLabelFilterOptions(menuLabels), [menuLabels]);
  const filteredMenus = useMemo(
    () => filterByMenuLabels(menus ?? [], selectedMenuLabelIds),
    [menus, selectedMenuLabelIds],
  );

  useEffect(() => {
    document.title = 'Menus';
    trackEvent('page_viewed', { page: 'menus' });
  }, []);

  const renderMenus = () => {
    if (menus.length === 0) {
      return (
        <EmptyState
          emptySectionText="Add your first menu"
          onClick={() => setIsMenuModalOpen(true)}
          buttonText="Create menu"
          type="menu"
          Icon={MenuIcon}
        />
      );
    }
    if (filteredMenus.length === 0) {
      return <Typography>No menus match your filters.</Typography>;
    }
    return (
      <CardGrid columnWidth={MENU_CARD_WIDTH}>
        {filteredMenus.map((menu: IMenu) => {
          const { menuId: menuID, name, image, consumerName } = menu;
          return (
            <MenuCard
              menuId={menuID}
              name={name}
              image={image}
              key={menuID}
              consumerName={consumerName}
              menuLabels={menu.menuLabels}
              sectionCount={menu.sectionCount}
            />
          );
        })}
      </CardGrid>
    );
  };

  return (
    <>
      {isMenusFetching ? (
        <SpinnerContainer>
          <Spinner size="xlarge" colour="rainbow" />
        </SpinnerContainer>
      ) : (
        <>
          <div className={styles['header-container']}>
            <BreadcrumbHeading
              breadcrumbs={[{ label: 'Menus', path: '/menus' }]}
              primaryButton={
                <PrimaryButton onClick={() => setIsMenuModalOpen(true)} icon={Plus}>
                  Create menu
                </PrimaryButton>
              }
            />
            {menus.length > 0 && (
              <div className={styles['filters']}>
                <MultiSelect
                  Icon={MenuIcon}
                  name="menu-labels"
                  label="Menu labels"
                  options={menuLabelFilterOptions}
                  selected={selectedMenuLabelIds}
                  onChange={setSelectedMenuLabelIds}
                  disabled={menuLabelFilterOptions.length === 0}
                />
              </div>
            )}
          </div>
          <MenuModal
            title="Create new menu"
            closeModal={() => setIsMenuModalOpen(false)}
            open={isMenuModalOpen}
            variant="create"
            image={null}
          />
          {renderMenus()}
        </>
      )}
    </>
  );
};

export default Menus;
